import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/order", label: "Custom" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled || open
          ? "backdrop-blur-xl bg-[#050505]/70 border-white/[0.06]"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="container-lux flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3.5">
          <img src="/logo.png" alt="MetalMuse logo" className="w-10 h-10 rounded-full ring-1 ring-white/15" />
          <span className="flex flex-col leading-none">
            <span className="font-tech text-lg font-semibold tracking-[0.12em] text-silver-gradient uppercase">MetalMuse</span>
            <span className="text-[9px] uppercase tracking-[0.35em] text-[#A1A1AA] mt-1">Made to Last</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.label}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[11px] font-semibold uppercase tracking-[0.25em] transition-colors duration-300 hover:text-cyan-300 ${
                  isActive ? "text-cyan-300" : "text-white/70"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/shop"
            data-testid="nav-shop-now-cta"
            className="text-[11px] font-semibold uppercase tracking-[0.25em] text-black bg-white px-7 py-3 rounded-full hover:bg-cyan-200 transition-[background-color,box-shadow,transform] duration-300 hover:shadow-[0_0_28px_rgba(0,229,255,0.35)] hover:-translate-y-0.5"
          >
            Shop Now
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <nav className="lg:hidden backdrop-blur-xl bg-[#050505]/85 border-t border-white/[0.06]" data-testid="nav-mobile">
          <div className="container-lux py-8 flex flex-col gap-6">
            {LINKS.map((l) => (
              <NavLink
                key={l.label}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-tech text-sm uppercase tracking-[0.25em] ${isActive ? "text-cyan-300" : "text-white/80"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/shop"
              data-testid="nav-mobile-shop-now-cta"
              className="bg-white text-black text-center text-xs font-semibold uppercase tracking-[0.25em] px-6 py-4 rounded-full mt-2"
            >
              Shop Now
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};
